<template>
  <div>
    <v-alert
      v-if="filters.dates.to && filters.dates.from !== filters.dates.to"
      color="error"
      type="error"
      dark
      class="mt-2"
    >
      {{ $t("daily_report") }}
    </v-alert>
    <template v-else>
      <v-row justify="end" align="end" class="mb-2">
        <v-col cols="auto">
          <text-field
            v-model="search"
            append-icon="mdi-magnify"
            :placeholder="$t('search')"
            hide-details
          ></text-field>
        </v-col>
        <v-col cols="auto">
          <dropdown
            :label="$t('view')"
            v-model="selectedLayout"
            :items="layouts"
            hide-details
          ></dropdown>
        </v-col>
        <v-col cols="auto">
          <shift-picker v-model="selectedShift" :shifts="shifts" />
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="customers"
        :loading="loading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item="{ item, index }">
          <tr>
            <td>{{ index + 1 }}</td>
            <td>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ item.fullname }}
                  </span>
                </template>
                <span>{{ item.userId }}</span>
              </v-tooltip>
            </td>
            <td>{{ item.date }}</td>
            <td>{{ item.shift }}</td>
            <template v-for="category in mealCategories">
              <td>
                <span v-if="item[category]">
                  <!-- Layout 0: Količine i nazive jela -->
                  <template v-if="selectedLayout === 0">
                    <strong>{{ item[category].quantity }}x</strong>
                    <v-tooltip bottom v-if="item[category].label !== $t('total')">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          {{ translate(item[category].food.name) }}
                        </span>
                      </template>
                      <span>{{ item[category].id }}</span>
                    </v-tooltip>
                    <span v-else>{{ item[category].label }}</span>
                  </template>
                  <!-- Layout 1: Nazive jela -->
                  <template v-else-if="selectedLayout === 1">
                    <v-tooltip bottom v-if="item[category].label !== $t('total')">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          {{ translate(item[category].food.name) }}
                        </span>
                      </template>
                      <span>{{ item[category].id }}</span>
                    </v-tooltip>
                    <span v-else>{{ item[category].label }}</span>
                  </template>
                  <!-- Layout 2: Količine jela -->
                  <template v-else-if="selectedLayout === 2">
                    <strong>{{ item[category].quantity }}</strong>
                  </template>
                </span>
              </td>
            </template>
            <td>{{ item.note }}</td>
          </tr>
        </template>
        <template v-slot:body.append>
          <tr style="background: #f5f5f5; font-weight: bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td></td>
            <td></td>
            <template v-for="category in mealCategories">
              <td>{{ getTotal(category) }}x</td>
            </template>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
export default {
  name: "CompanyReportTabCustomers",
  mixins: [hasReportPrinting, hasPrinting, hasExcel],
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedShift: null,
      selectedLayout: 0,
      search: "",
      searchDebounced: "",
      searchTimeout: null,
    };
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    mealCategories() {
      return this.users.reduce((acc, user) => {
        (user.orders || []).forEach((order) => {
          if (order.food?.category?.name && !acc.includes(order.food.category.name)) {
            acc.push(order.food.category.name);
          }
        });
        return acc;
      }, []);
    },
    customers() {
      let filteredUsers = this.users.filter(
        (user) => user.meals && user.shift === this.selectedShift
      );

      // Primeni search filter ako postoji
      if (this.searchDebounced && this.searchDebounced.trim()) {
        const searchTerm = this.searchDebounced.toLowerCase().trim();
        filteredUsers = filteredUsers.filter((user) => {
          const fullname = [user.name, user.lastname].join(" ").toLowerCase();
          const userId = (user.userId || "").toString().toLowerCase();
          const note = (user.note || "").toLowerCase();

          // Pretraži i po imenima jela u narudžbama
          const foodNames = (user.orders || [])
            .map((order) => order.food?.name || "")
            .join(" ")
            .toLowerCase();

          // Pretraži po imenu, prezimenu, user ID-u, napomeni ili imenima jela
          return (
            fullname.includes(searchTerm) ||
            userId.includes(searchTerm) ||
            note.includes(searchTerm) ||
            foodNames.includes(searchTerm)
          );
        });
      }

      return filteredUsers.map((user) => {
        const payload = {
          ...user,
          fullname: [user.name, user.lastname].join(" "),
        };
        this.mealCategories.forEach((category) => {
          const categoryOrders = user.orders.filter(
            (order) => order.food?.category?.name === category
          );
          if (categoryOrders.length) {
            payload[category] = {
              ...categoryOrders[0],
              quantity: categoryOrders.reduce((sum, order) => sum + order.quantity, 0),
            };
          }
        });
        return payload;
      });
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("name_surname"), value: "fullname" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift" },
        ...this.mealCategories.map((category) => ({
          text: this.translate(category),
          value: category,
        })),
        { text: this.$t("note"), value: "note" },
      ];
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.name)
        .join(", ");
    },
    shifts() {
      return [...new Set(this.users.flatMap((item) => item.shift))];
    },
    filters() {
      return this.$store.state.report.filters;
    },
    totals() {
      const result = {};
      this.mealCategories.forEach((category) => {
        result[category] = this.customers.reduce((sum, item) => {
          return item[category] ? sum + item[category].quantity : sum;
        }, 0);
      });
      return result;
    },
    company() {
      return this.$store.state.company;
    },
  },
  methods: {
    getTotal(category) {
      return this.totals[category] || 0;
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  watch: {
    search: {
      handler(newSearch) {
        // Debounce search za 300ms
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.searchDebounced = newSearch;
        }, 300);
      },
    },
    shifts: {
      handler() {
        if (this.shifts.length) {
          this.selectedShift = this.shifts[0];
        } else {
          this.selectedShift = null;
        }
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const customers = this.customers.map((customer, index) => {
          const newCustomer = { num: index + 1, ...customer };
          this.mealCategories.forEach((category) => {
            if (newCustomer[category]) {
              // Layout 0: Količine i nazive jela
              if (this.selectedLayout === 0) {
                newCustomer[category] = [
                  newCustomer[category].quantity,
                  newCustomer[category].food.name,
                ].join("x ");
              }
              // Layout 1: Nazive jela
              else if (this.selectedLayout === 1) {
                newCustomer[category] = newCustomer[category].food.name;
              }
              // Layout 2: Količine jela
              else if (this.selectedLayout === 2) {
                newCustomer[category] = newCustomer[category].quantity;
              }
            }
          });
          return newCustomer;
        });
        this.printDocument({
          tables: [
            {
              headers: this.headers,
              items: customers,
            },
          ],
          title: [this.$t("report"), this.$t("orderers")].join(" "),
          subtitle: this.subtitle,
          beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const customers = this.customers.map((customer, index) => {
          const newCustomer = { num: index + 1, ...customer };
          this.mealCategories.forEach((category) => {
            if (newCustomer[category]) {
              // Layout 0: Količine i nazive jela
              if (this.selectedLayout === 0) {
                newCustomer[category] = [
                  newCustomer[category].quantity,
                  newCustomer[category].food.name,
                ].join("x ");
              }
              // Layout 1: Nazive jela
              else if (this.selectedLayout === 1) {
                newCustomer[category] = newCustomer[category].food.name;
              }
              // Layout 2: Količine jela
              else if (this.selectedLayout === 2) {
                newCustomer[category] = newCustomer[category].quantity;
              }
            }
          });
          return newCustomer;
        });
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("orderers")].join(" ") + ".xlsx",
          sheets: [
            {
              name: [this.$t("report"), this.$t("orderers")].join(" "),
              headers: this.headers,
              items: customers,
              title: this.$t("users_and_ordered_meals"),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${
                this.selectedCompanies || ""
              }</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
